import React from "react";
import log from "loglevel";
import { Action } from "typescript-fsa";
import { Map } from "immutable";
import { FormattedMessage } from "react-intl";
import { SubmissionError, FormErrors } from "redux-form";
import { Form, Button } from "reactstrap";

import { Spinner } from "@ui/Spinner";
import { ApiRequest } from "../../requests";

export interface AsyncFormStateProps {
    requests?: Map<string, ApiRequest>;
}

export interface AsyncFormOwnProps extends React.Props<AsyncForm> {
    submitText: string;
    actionCreator: (data: any) => Action<ApiRequest>;
    handleSubmit: (submit: () => Promise<any>) => any;
    onSuccess?: () => void;
    error?: string;
}

export interface AsyncFormState {
    loading: boolean;
    currentRequest: ApiRequest;
    resolve: (value?: any) => void;
    reject: (reason?: Error) => void;
}

type Props = AsyncFormOwnProps & AsyncFormStateProps;

export class AsyncForm extends React.Component<Props, AsyncFormState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            loading: false,
            currentRequest: null,
            resolve: null,
            reject: null,
        };
    }

    public componentWillReceiveProps(nextProps: Props) {
        // Check if the request has updated
        const { currentRequest, resolve, reject } = this.state;
        if (currentRequest != null) {
            const nextRequest = nextProps.requests.get(currentRequest.uuid);
            // If the request exists and has changed state.
            if (nextRequest != null && currentRequest.state !== nextRequest.state) {
                const nextState: AsyncFormState = {
                    currentRequest: nextRequest,
                    loading: false,
                    resolve,
                    reject,
                };
                // Set loading if in progress.
                if (nextRequest.isInProgress()) {
                    nextState.loading = true;
                }
                // If it's completed successfully, fire resolve and onSuccess.
                if (nextRequest.isFinishedSucceeded()) {
                    // Resolve the promise.
                    resolve();

                    if (nextProps.onSuccess != null) {
                        nextProps.onSuccess();
                    }
                }
                if (nextRequest.isFinishedFailed()) {
                    // Build the error object
                    const requestObj = nextRequest.toJS();
                    const errors = {} as FormErrors<any>;
                    if (requestObj.error != null) {
                        // Convert the error if it's immutable
                        const error = requestObj.error;

                        if (error.errors != null &&
                            error.errors.length > 0
                        ) {
                            // Looks like there are validation errors, put them together
                            for (const valError of error.errors) {
                                if (valError.key != null) {
                                    errors[valError.key] = valError.message;
                                }
                            }
                        }
                        else {
                            // It's a general error, set that.
                            errors._error = error.message;
                        }
                    }

                    // Reject the promise.
                    reject(new SubmissionError(errors));
                }
                this.setState(nextState);
            }
        }
    }

    private handleSubmit(data: any): Promise<any> {
        const { actionCreator } = this.props;
        if (actionCreator != null) {
            if (!this.state.loading) {
                const self = this;
                const promise = new Promise(
                    (resolve: (value?: any) => void, reject: (reason?: any) => void) => {
                        const action = actionCreator(data);

                        // Set loading only if we get a request
                        self.setState({
                            loading: action.payload != null,
                            currentRequest: action.payload,
                            resolve,
                            reject,
                        });
                    },
                );
                return promise;
            }
        }
        else {
            const errorMessage = "Cannot perform submit without a supplied 'actionCreator' action";
            log.error(errorMessage);
            return Promise.reject(new SubmissionError({_error: errorMessage}));
        }
    }

    public render(): JSX.Element {
        let submitText = (
            <span>{this.props.submitText}</span>
        );
        let buttonDisabled = false;
        if (this.state.loading) {
            submitText = (
                <span>
                    <Spinner inline color="light" />
                    <FormattedMessage id="ui.async.loading"
                        defaultMessage="Loading" />
                </span>
            );
            buttonDisabled = true;
        }
        return (
            <Form onSubmit={this.props.handleSubmit(this.handleSubmit.bind(this))}>
                {this.props.error &&
                    <p className="alert alert-danger" role="alert">
                        {this.props.error}
                    </p>
                }
                {this.props.children}

                <Button type="submit" color="primary"
                    disabled={buttonDisabled}>{submitText}</Button>
            </Form>
        );
    }
}
